import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Truck, CreditCard, Banknote, Lock } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { submitOrder } from "../lib/api";

const FIELD = "w-full rounded-2xl border border-[var(--sss-border)] bg-white px-4 py-3 text-sm focus:outline-none focus:border-[var(--sss-pink)] focus:ring-2 focus:ring-[var(--sss-pink)]/20 transition";

export default function Checkout() {
    const { items, subtotal, clearCart } = useCart();
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [form, setForm] = useState({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        fulfillment: "delivery",
        delivery_address: "",
        requested_date: "",
        notes: "",
        payment_method: "card",
    });

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const { user } = useAuth();
    useEffect(() => {
        if (!user) return;
        setForm((f) => ({
            ...f,
            customer_name: f.customer_name || user.name || "",
            customer_email: f.customer_email || user.email || "",
        }));
    }, [user]);

    if (items.length === 0) {
        return (
            <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="checkout-empty">
                <h2 className="font-serif-display text-3xl">Your cart is empty</h2>
                <p className="mt-3 text-[var(--sss-text-muted)]">Add some sweet treats before checking out.</p>
                <button type="button" onClick={() => navigate("/menu")} className="btn-pink mt-6" data-testid="checkout-empty-shop">
                    Shop the Menu
                </button>
            </div>
        );
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            const payload = {
                customer_name: form.customer_name,
                customer_email: form.customer_email,
                customer_phone: form.customer_phone,
                fulfillment: "delivery",
                delivery_address: form.delivery_address,
                requested_date: form.requested_date,
                notes: form.notes,
                payment_method: form.payment_method,
                origin_url: window.location.origin,
                items: items.map((it) => ({
                    product_id: it.product_id,
                    size: it.size,
                    quantity: it.quantity,
                })),
            };
            const res = await submitOrder(payload);
            clearCart();
            if (form.payment_method === "card" && res.checkout_url) {
                window.location.href = res.checkout_url;
                return;
            }
            navigate(`/order-confirmation/${res.order.id}`);
        } catch (err) {
            setError(err?.response?.data?.detail || "Could not place order. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-12" data-testid="checkout-page">
            <h1 className="font-serif-display text-4xl md:text-5xl font-bold text-center">Checkout</h1>
            <p className="text-center text-[var(--sss-text-muted)] mt-2 text-sm">Pay securely with card, or choose a manual option (Cash App, PayPal, Apple Pay, Cash).</p>

            <div className="mt-10 grid lg:grid-cols-3 gap-8">
                <form onSubmit={handleSubmit} className="lg:col-span-2 bg-white rounded-3xl sss-shadow p-6 md:p-8 space-y-5" data-testid="checkout-form">
                    <h2 className="font-serif-display text-2xl font-semibold">Your Information</h2>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)]" htmlFor="co-name">Full Name *</label>
                            <input id="co-name" required value={form.customer_name} onChange={update("customer_name")} className={FIELD + " mt-1"} data-testid="checkout-name" />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)]" htmlFor="co-email">Email *</label>
                            <input id="co-email" type="email" required value={form.customer_email} onChange={update("customer_email")} className={FIELD + " mt-1"} data-testid="checkout-email" />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)]" htmlFor="co-phone">Phone *</label>
                            <input id="co-phone" required value={form.customer_phone} onChange={update("customer_phone")} className={FIELD + " mt-1"} data-testid="checkout-phone" />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)]" htmlFor="co-date">Needed By</label>
                            <input id="co-date" type="date" value={form.requested_date} onChange={update("requested_date")} className={FIELD + " mt-1"} data-testid="checkout-date" />
                        </div>
                    </div>

                    <div>
                        <p className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)] mb-2">Fulfillment</p>
                        <div className="px-4 py-3 rounded-2xl border-2 border-[var(--sss-pink)] bg-[var(--sss-blush)] flex items-center gap-3" data-testid="checkout-fulfillment-delivery">
                            <Truck size={18} className="text-[var(--sss-pink)]" />
                            <div className="text-left">
                                <p className="text-sm font-semibold">Delivery</p>
                                <p className="text-xs text-[var(--sss-text-muted)]">We deliver locally — enter your address below</p>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)]" htmlFor="co-addr">Delivery Address *</label>
                        <textarea id="co-addr" rows={2} required value={form.delivery_address} onChange={update("delivery_address")} className={FIELD + " mt-1 resize-none"} placeholder="Street, City, State, ZIP" data-testid="checkout-address" />
                    </div>

                    <div>
                        <p className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)] mb-2">Payment Method</p>
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => setForm((f) => ({ ...f, payment_method: "card" }))}
                                className={`px-4 py-3 rounded-2xl border-2 flex items-center gap-3 transition text-left ${form.payment_method === "card" ? "border-[var(--sss-pink)] bg-[var(--sss-blush)]" : "border-[var(--sss-border)]"}`}
                                data-testid="checkout-payment-card"
                            >
                                <CreditCard size={18} className="text-[var(--sss-pink)]" />
                                <div>
                                    <p className="text-sm font-semibold flex items-center gap-1.5">Pay with Card <Lock size={11} /></p>
                                    <p className="text-xs text-[var(--sss-text-muted)]">Secure Stripe checkout</p>
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={() => setForm((f) => ({ ...f, payment_method: "manual" }))}
                                className={`px-4 py-3 rounded-2xl border-2 flex items-center gap-3 transition text-left ${form.payment_method === "manual" ? "border-[var(--sss-pink)] bg-[var(--sss-blush)]" : "border-[var(--sss-border)]"}`}
                                data-testid="checkout-payment-manual"
                            >
                                <Banknote size={18} className="text-[var(--sss-pink)]" />
                                <div>
                                    <p className="text-sm font-semibold">Pay Manually</p>
                                    <p className="text-xs text-[var(--sss-text-muted)]">Cash App · PayPal · Apple Pay · Cash</p>
                                </div>
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="text-xs uppercase tracking-wider font-semibold text-[var(--sss-text-muted)]" htmlFor="co-notes">Order Notes</label>
                        <textarea id="co-notes" rows={3} value={form.notes} onChange={update("notes")} className={FIELD + " mt-1 resize-none"} placeholder="Color preferences, decorations, allergies…" data-testid="checkout-notes" />
                    </div>

                    {error && <p className="text-sm text-red-600" data-testid="checkout-error">{error}</p>}

                    <button type="submit" disabled={submitting} className="btn-pink w-full justify-center" data-testid="checkout-submit">
                        {submitting ? "Placing Order…" : form.payment_method === "card" ? <>Pay with Card <Lock size={14} /></> : <>Place Order <Heart size={14} fill="#fff" strokeWidth={0} /></>}
                    </button>
                    <p className="text-xs text-center text-[var(--sss-text-muted)]">
                        {form.payment_method === "card" ? "You'll be redirected to Stripe for secure card entry." : "We'll show payment instructions on the next page."}
                    </p>
                </form>

                <aside className="bg-white rounded-3xl sss-shadow p-6 md:p-8 h-fit lg:sticky lg:top-24" data-testid="checkout-summary">
                    <h2 className="font-serif-display text-2xl font-semibold">Order Summary</h2>
                    <ul className="mt-4 divide-y divide-[var(--sss-border)]">
                        {items.map((it) => (
                            <li key={`${it.product_id}-${it.size}`} className="py-3 flex justify-between gap-3 text-sm">
                                <div>
                                    <p className="font-medium">{it.product_name}</p>
                                    <p className="text-xs text-[var(--sss-text-muted)]">{it.size} × {it.quantity}</p>
                                </div>
                                <p className="font-serif-display font-semibold">${(it.unit_price * it.quantity).toFixed(2)}</p>
                            </li>
                        ))}
                    </ul>
                    <div className="border-t border-[var(--sss-border)] pt-4 mt-4 flex justify-between items-center">
                        <span className="font-medium">Total</span>
                        <span className="font-serif-display text-2xl font-bold">${subtotal.toFixed(2)}</span>
                    </div>
                </aside>
            </div>
        </div>
    );
}
